import { useEffect, useState } from 'react';

/**
 * Available themes:
 * - classic: Original blue/grey production look
 * - light: New light mode with solid maroon primary
 * - dark: New dark mode with neon maroon primary
 */
export type Theme = 'classic' | 'light' | 'dark';

const STORAGE_KEY = 'sr-theme';
const VALID_THEMES: Theme[] = ['classic', 'light', 'dark'];

export function useTheme(): {
  theme: Theme;
  toggle: () => void;
  cycle: () => void;
  setTheme: (t: Theme) => void;
  isDark: boolean;
} {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored && VALID_THEMES.includes(stored)) return stored;
    // Default to dark mode
    return 'dark';
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);

  // Toggle between light and dark only
  const toggle = () =>
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));

  // Cycle through all three themes: classic -> light -> dark -> classic
  const cycle = () =>
    setThemeState((prev) => {
      const idx = VALID_THEMES.indexOf(prev);
      return VALID_THEMES[(idx + 1) % VALID_THEMES.length];
    });

  return { theme, toggle, cycle, setTheme, isDark: theme === 'dark' };
}
